import {
  Component, Input, Output, EventEmitter, ContentChild, TemplateRef,
  ViewChild, ElementRef, AfterViewChecked, OnDestroy, NgZone, ChangeDetectorRef,
} from '@angular/core';

export type PopoverPlacement = 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end';

/**
 * `app-popover` — PRIMITIVO genérico de popover/menu flutuante, LIVRE de Angular
 * Material e de @angular/cdk. Posicionamento próprio (sem Floating UI — ver D1 no
 * spec: o ambiente Angular 11/TS 4.1 + npm travado inviabiliza a lib; o posicionador
 * mínimo aqui é trocável e mais alinhado com a meta de independência).
 *
 * Conteúdo LAZY: a projeção é um `<ng-template>` renderizado SÓ quando aberto — então
 * em listas grandes você paga só o gatilho por célula; o menu instancia uma vez, ao abrir.
 *
 * Uso:
 *   <button #t (click)="pop.toggle()" [attr.aria-expanded]="pop.open" aria-haspopup="listbox">…</button>
 *   <app-popover #pop [anchor]="t" placement="bottom-start">
 *     <ng-template>
 *       <div class="meu-menu" role="listbox"> … </div>
 *     </ng-template>
 *   </app-popover>
 *
 * Responsabilidades (a11y/UX): posicionar (flip vertical + shift na viewport),
 * `aria-expanded` no gatilho, `Esc`/click-fora → fechar + devolver foco, foco no
 * painel ao abrir, reposicionar em scroll/resize. A navegação por teclado DENTRO do
 * menu (setas/enter) é responsabilidade do CONTEÚDO projetado.
 *
 * Limitação conhecida (v1): usa `position: fixed` no lugar (sem body-portal). Não é
 * recortado por `overflow:hidden/scroll`, MAS um ancestral com `transform`/`filter`/
 * `will-change` vira o containing block do fixed. Se isso ocorrer ao plugar na tabela
 * (Fase 2), adicionamos um portal pro <body>. A API pública não muda.
 */
/**
 * z-index do painel quando o gatilho está dentro de um overlay do CDK.
 * Um degrau acima do `.cdk-overlay-container` (2010 em `_color-scheme.scss`).
 */
const POPOVER_Z_IN_OVERLAY = 2020;

@Component({
  selector: 'app-popover',
  templateUrl: './popover.component.html',
  styleUrls: ['./popover.component.scss'],
})
export class PopoverComponent implements AfterViewChecked, OnDestroy {
  /** Elemento-gatilho contra o qual o painel é posicionado (e que recebe `aria-expanded`). */
  @Input() anchor: HTMLElement;
  @Input() placement: PopoverPlacement = 'bottom-start';
  /** Distância (px) entre o gatilho e o painel. */
  @Input() offset = 4;
  /** Respiro mínimo (px) das bordas da viewport. */
  @Input() viewportPadding = 8;
  /**
   * Backdrop transparente que captura o ponteiro enquanto aberto. `true` (default) para
   * popovers acionados por CLIQUE (menus de grupo/status): impede hover atrás e fecha no
   * clique-fora. Para popovers acionados por HOVER, passe `false` — o backdrop bloquearia o
   * próprio hover; nesse caso o consumidor controla abrir/fechar via mouseenter/mouseleave.
   */
  @Input() hasBackdrop = true;
  /** Classe extra adicionada ao `.jems-popover-panel` — permite estilizar
   *  via CSS global um popover específico (ex.: travar width). */
  @Input() panelClass = '';
  /**
   * Trava o scroll da PÁGINA (scroller da viewport) enquanto o popover está aberto —
   * a página não sobe/desce; o painel fica "congelado" no lugar em vez de acompanhar o
   * scroll via reposição. Compensa a largura da barra de rolagem (padding-right) para o
   * layout não "pular" ao esconder/mostrar a barra. Default `false` (comportamento antigo:
   * o painel reposiciona ao rolar). Use `true` para painéis-diálogo (ex.: seleção de tópicos).
   */
  @Input() lockScroll = false;

  @Output() opened = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();
  @Output() openChange = new EventEmitter<boolean>();   // habilita [(open)]

  @ContentChild(TemplateRef) contentTpl: TemplateRef<unknown>;
  @ViewChild('panel') panelRef: ElementRef<HTMLElement>;
  @ViewChild('backdrop') backdropRef: ElementRef<HTMLElement>;

  private _open = false;
  private _needsPosition = false;
  private _portaled = false;
  private _scrollParents: HTMLElement[] = [];
  private readonly _onDocPointer = (e: Event) => this.onDocumentPointer(e);
  private readonly _onKeydown = (e: KeyboardEvent) => this.onKeydown(e);
  private readonly _onReposition = () => this.position();

  // MENU ITEM SELECTION FEEDBACK — comportamento macOS.
  // No macOS, ao clicar num item de menu nativo, o item pisca (bg accent
  // → OFF → ON) antes do menu fechar, dando confirmação visual da escolha.
  // Detectamos plataforma via `navigator.platform` (ainda widely-supported
  // pra macOS/Windows/Linux discrimination — a alternativa `userAgentData`
  // ainda tem suporte irregular). Só ativa em Mac; outros SOs continuam
  // com fechamento imediato (comportamento nativo esperado nesses).
  private static readonly IS_MAC = typeof navigator !== 'undefined'
    && /Mac|iPhone|iPad|iPod/i.test(navigator.platform);
  /** Flag pra ligar/desligar o efeito de piscar (macOS selection blink) sem
   *  apagar a implementação. Mudar pra `true` reativa o comportamento
   *  descrito no `flashItemThenClose()`. Motivo do desligamento: preferência
   *  visual — o fechamento imediato lê mais snappy. */
  private static readonly ENABLE_MAC_SELECTION_BLINK = false;
  /** Último item que recebeu click no painel. Setado pelo delegate capture
   *  (roda ANTES do handler do consumer) e consumido pelo `hide()` pra
   *  aplicar o flash antes de fechar. */
  private lastClickedItem: HTMLElement | null = null;
  /** Timers do flash — cancelados no `ngOnDestroy` e no re-open. */
  private flashTimers: number[] = [];
  private readonly _onPanelClick = (e: Event) => this.onPanelClick(e);

  constructor(
    private zone: NgZone,
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef<HTMLElement>,
  ) {}

  @Input()
  get open(): boolean { return this._open; }
  set open(v: boolean) { v ? this.show() : this.hide(); }

  toggle(): void { this._open ? this.hide() : this.show(); }

  show(): void {
    if (this._open) { return; }
    this._open = true;
    this._needsPosition = true;
    this.setAnchorExpanded(true);
    // Listeners fora do Angular: scroll/resize não devem disparar change detection.
    this.zone.runOutsideAngular(() => {
      document.addEventListener('mousedown', this._onDocPointer, true);
      document.addEventListener('touchstart', this._onDocPointer, true);
      document.addEventListener('keydown', this._onKeydown, true);
      window.addEventListener('scroll', this._onReposition, true);
      window.addEventListener('resize', this._onReposition, true);
      // O painel vive no <body> (portal), então NÃO acompanha o scroll da página sozinho.
      // O scroll capture no window nem sempre cobre containers internos roláveis (mat-sidenav,
      // .main-body com overflow…); então escutamos o scroll de CADA ancestral rolável do
      // gatilho e reposicionamos. É o que mantém o menu colado ao botão ao rolar.
      this._scrollParents = this.collectScrollParents();
      this._scrollParents.forEach(p => p.addEventListener('scroll', this._onReposition, { passive: true }));
    });
    // Reset do estado de flash a cada abertura (paranoia contra timers órfãos
    // de open/close rápidos consecutivos).
    this.lastClickedItem = null;
    this.clearFlashTimers();
    if (this.lockScroll) { this.lockPageScroll(); }
    this.openChange.emit(true);
    this.opened.emit();
    this.cdr.markForCheck();
  }

  hide(): void {
    if (!this._open) { return; }
    // Comportamento macOS: se hide() foi disparado por click num item, PISCA
    // o item (accent bg → OFF → ON) antes de fechar, dando confirmação da
    // seleção — como os menus nativos do macOS fazem. Outros SOs fecham
    // imediato (esperado). Delay total ~180ms (2 blinks de 60ms cada).
    if (PopoverComponent.ENABLE_MAC_SELECTION_BLINK
        && PopoverComponent.IS_MAC
        && this.lastClickedItem) {
      const item = this.lastClickedItem;
      this.lastClickedItem = null;
      this.flashItemThenClose(item);
      return;
    }
    this.doClose();
  }

  /** Fecha o popover sem flash (usado direto quando não é macOS ou quando
   *  o fechamento não vem de click num item — Esc, click fora, hide programático). */
  private doClose(): void {
    // Devolve os nós portados ao host ANTES do *ngIf destruí-los (o renderer remove
    // relativo ao parent original; nó órfão no <body> faria removeChild lançar).
    this.restoreFromBody();
    this._open = false;
    this._needsPosition = false;
    this.unlockPageScroll();
    this.removeListeners();
    this.setAnchorExpanded(false);
    this.returnFocus();
    this.openChange.emit(false);
    this.closed.emit();
    this.cdr.markForCheck();
  }

  /** Delegate CAPTURE do click no painel — roda ANTES do handler do consumer
   *  (que geralmente chama `pop.hide()`). Registra o alvo pra `hide()` saber
   *  qual item piscar. */
  private onPanelClick(ev: Event): void {
    const target = ev.target as HTMLElement | null;
    if (!target) return;
    // Match items via classes usuais dos menus JEMS3 (todos os popovers usam
    // esses padrões — `.mat-menu-item` legado, ou `[role="menuitem"]` ARIA).
    const item = target.closest('.mat-menu-item, [role="menuitem"]') as HTMLElement | null;
    this.lastClickedItem = item;
  }

  /** macOS-style selection blink: 2 blinks (OFF → ON → OFF → ON) antes de
   *  fechar — mais fiel aos menus nativos do macOS que fazem exatamente esse
   *  padrão. Cadência ~60ms/estado = ~240ms total, perceptível como
   *  "confirmação da escolha" sem prender o usuário. Usa `style.setProperty`
   *  com `important` (JS inline) porque as regras `:hover` do menu chair-
   *  style usam `!important` com specificity (0,4,0) — CSS-only não vence.
   *  Inline `!important` bate qualquer stylesheet. */
  private flashItemThenClose(item: HTMLElement): void {
    const STEP = 60;   // duração de cada estado (OFF ou ON)
    // Ler cor "on" do hover ATUAL do item (getComputedStyle já resolve
    // qualquer !important vencedor no cascade). Cache pra reaplicar nos
    // ON's sem depender do estado :hover (mouse pode ter saído).
    const onColor = getComputedStyle(item).backgroundColor;
    const off = () => item.style.setProperty('background-color', 'transparent', 'important');
    const on  = () => item.style.setProperty('background-color', onColor, 'important');
    // Sequência: OFF (t=0) → ON (t=60) → OFF (t=120) → ON (t=180) → close (t=240).
    const t1 = window.setTimeout(off, 0);
    const t2 = window.setTimeout(on,  STEP);
    const t3 = window.setTimeout(off, STEP * 2);
    const t4 = window.setTimeout(on,  STEP * 3);
    const t5 = window.setTimeout(() => {
      item.style.removeProperty('background-color');
      this.doClose();
    }, STEP * 4);
    this.flashTimers.push(t1, t2, t3, t4, t5);
  }

  private clearFlashTimers(): void {
    this.flashTimers.forEach(id => clearTimeout(id));
    this.flashTimers = [];
  }

  ngAfterViewChecked(): void {
    // Posiciona e foca UMA vez, depois que o *ngIf renderizou o painel.
    if (this._open && this._needsPosition && this.panelRef) {
      this._needsPosition = false;
      this.portalToBody();   // mover pro <body> ANTES de posicionar (imune a ancestral c/ transform)
      // Garante que `panelClass` esteja no DOM ANTES de measure/position
      // (caso [ngClass] ainda não tenha aplicado por timing de CD).
      const p = this.panelRef && this.panelRef.nativeElement;
      if (p && this.panelClass) {
        this.panelClass.split(/\s+/).forEach(c => c && p.classList.add(c));
      }
      // Delegate CAPTURE de click no painel: registra o item clicado ANTES do
      // handler `(click)` do consumer disparar `pop.hide()`. Fora da NgZone
      // (não muda binding — só cache de referência de nó DOM).
      if (p) {
        this.zone.runOutsideAngular(() => p.addEventListener('click', this._onPanelClick, true));
      }
      this.position();
      this.focusPanel();
      // Reposiciona no PRÓXIMO frame: nesta 1ª passada (logo após o portal pro
      // <body>) o layout pode não estar liquidado — o rect do containing block
      // do `fixed` ainda assenta, então a 1ª medida sai deslocada e o menu só
      // "saltava" pro lugar no 1º scroll/resize (que re-dispara position()).
      // O rAF roda fora do Angular (sem CD) depois do reflow → posição correta
      // já na abertura. Guard `_open` evita reposicionar após fechar.
      this.zone.runOutsideAngular(() => {
        requestAnimationFrame(() => { if (this._open) { this.position(); } });
      });
    }
  }

  ngOnDestroy(): void {
    this.unlockPageScroll();  // não deixa a página travada se destruído enquanto aberto
    this.removeListeners();
    this.clearFlashTimers();  // evita timers órfãos disparando após destroy
    this.restoreFromBody();   // evita nós órfãos no <body> se destruído enquanto aberto
  }

  // ── Trava de scroll da página (opt-in via [lockScroll]) ──────────────────────
  // Congela o scroller da viewport (document.scrollingElement = <html> em standards
  // mode) com overflow:hidden, preservando a posição atual. Compensa a barra de
  // rolagem com padding-right pra o conteúdo não deslocar quando a barra some.
  private _lockedScroller: { el: HTMLElement; overflow: string; paddingRight: string } | null = null;

  private lockPageScroll(): void {
    if (this._lockedScroller) { return; }
    const el = (document.scrollingElement as HTMLElement) || document.documentElement;
    if (!el) { return; }
    const sbw = window.innerWidth - document.documentElement.clientWidth;   // largura da barra
    this._lockedScroller = { el, overflow: el.style.overflow, paddingRight: el.style.paddingRight };
    if (sbw > 0) {
      const cur = parseFloat(getComputedStyle(el).paddingRight) || 0;
      el.style.paddingRight = `${cur + sbw}px`;
    }
    el.style.overflow = 'hidden';
  }

  private unlockPageScroll(): void {
    if (!this._lockedScroller) { return; }
    const { el, overflow, paddingRight } = this._lockedScroller;
    el.style.overflow = overflow;
    el.style.paddingRight = paddingRight;
    this._lockedScroller = null;
  }

  /**
   * Portal pro <body>: painel e backdrop saem de qualquer ancestral com
   * transform/filter/will-change/overflow da página, então `position: fixed` volta a
   * ser relativo à viewport. Seguro p/ tema: a classe `.dark-theme` vive no <body>, então
   * o painel portado continua sob ela — `:host-context(.dark-theme)` do conteúdo projetado
   * casa normalmente. (Regras `.dark-theme …` de conteúdo de componente Emulated precisam
   * ser GLOBAIS, pois o _ngcontent no `.dark-theme` impediria de casar o <body>.)
   */
  private portalToBody(): void {
    if (this._portaled) { return; }
    const backdrop = this.backdropRef && this.backdropRef.nativeElement;
    const panel = this.panelRef && this.panelRef.nativeElement;
    const host = this.portalHost();
    if (backdrop) { host.appendChild(backdrop); }
    if (panel) { host.appendChild(panel); }
    this._portaled = true;
    this.liftAboveOverlayContainer();
  }

  /**
   * Onde o painel é portado.
   *
   * Regra geral: `<body>`. MAS quando o gatilho está dentro de um overlay do
   * CDK (mat-dialog, bottom sheet), o destino é o PRÓPRIO
   * `.cdk-overlay-container`. Motivo: aquele container tem `z-index` e cria um
   * contexto de empilhamento; um painel no `<body>` disputa com o container
   * INTEIRO, e qualquer regra que suba o container passa por cima do popover —
   * foi o que fez o visualizador de fusos abrir atrás do diálogo. Dentro do
   * container, a comparação passa a ser com os irmãos (o painel do diálogo,
   * `z-index: 1000`), que o `liftAboveOverlayContainer` vence com folga.
   *
   * O container do CDK é `position: fixed` e sem `transform`, então o
   * `position: fixed` do painel continua relativo à viewport — o motivo
   * original do portal segue satisfeito.
   */
  private portalHost(): HTMLElement {
    const anchorEl = this.anchor as HTMLElement;
    const overlayContainer = anchorEl && anchorEl.closest
      ? anchorEl.closest('.cdk-overlay-container') as HTMLElement
      : null;
    return overlayContainer || document.body;
  }

  /**
   * Popover aberto de DENTRO de um overlay do CDK (mat-dialog, bottom sheet):
   * sobe o painel acima do container do overlay.
   *
   * Complementa o `portalHost`: já dentro do container, o painel precisa ficar
   * acima do painel do diálogo (`.cdk-overlay-pane`, `z-index: 1000`). Fora de
   * overlays nada muda — a ordem normal (tooltip > popover > chrome) é mantida.
   */
  private liftAboveOverlayContainer(): void {
    const anchorEl = this.anchor as HTMLElement;
    if (!anchorEl || !anchorEl.closest || !anchorEl.closest('.cdk-overlay-container')) { return; }

    const panel = this.panelRef && this.panelRef.nativeElement;
    const backdrop = this.backdropRef && this.backdropRef.nativeElement;
    if (panel) { panel.style.zIndex = `${POPOVER_Z_IN_OVERLAY}`; }
    if (backdrop) { backdrop.style.zIndex = `${POPOVER_Z_IN_OVERLAY - 1}`; }
  }

  /** Devolve painel e backdrop ao host (pré-requisito p/ o *ngIf destruí-los sem erro). */
  private restoreFromBody(): void {
    if (!this._portaled) { return; }
    const host = this.elementRef.nativeElement;
    const backdrop = this.backdropRef && this.backdropRef.nativeElement;
    const panel = this.panelRef && this.panelRef.nativeElement;
    // Remove o listener de click do panel ANTES de mover (paranoia contra
    // vazamento — o *ngIf vai destruir o nó em seguida).
    if (panel) { panel.removeEventListener('click', this._onPanelClick, true); }
    if (backdrop) { host.appendChild(backdrop); }
    if (panel) { host.appendChild(panel); }
    this._portaled = false;
  }

  private removeListeners(): void {
    document.removeEventListener('mousedown', this._onDocPointer, true);
    document.removeEventListener('touchstart', this._onDocPointer, true);
    document.removeEventListener('keydown', this._onKeydown, true);
    window.removeEventListener('scroll', this._onReposition, true);
    window.removeEventListener('resize', this._onReposition, true);
    this._scrollParents.forEach(p => p.removeEventListener('scroll', this._onReposition));
    this._scrollParents = [];
  }

  /** Ancestrais roláveis do gatilho (overflow auto/scroll/overlay), do mais próximo à raiz. */
  private collectScrollParents(): HTMLElement[] {
    const out: HTMLElement[] = [];
    let node: HTMLElement | null = this.anchor ? this.anchor.parentElement : null;
    while (node) {
      const s = getComputedStyle(node);
      if (/(auto|scroll|overlay)/.test(`${s.overflowY}${s.overflowX}`)) { out.push(node); }
      node = node.parentElement;
    }
    return out;
  }

  /**
   * Cursor do backdrop: emula a "mãozinha" do gatilho quando o ponteiro passa sobre o
   * retângulo dele (o backdrop cobre o gatilho, então herdaríamos a seta). Baseado em
   * getBoundingClientRect → robusto a contextos de empilhamento (z-index não resolveria).
   * Set imperativo no elemento (sem binding) pra não criar trabalho de render por movimento.
   */
  onBackdropMove(e: MouseEvent): void {
    const backdrop = e.currentTarget as HTMLElement;
    if (!this.anchor) { backdrop.style.cursor = 'default'; return; }
    const r = this.anchor.getBoundingClientRect();
    const overAnchor =
      e.clientX >= r.left && e.clientX <= r.right &&
      e.clientY >= r.top && e.clientY <= r.bottom;
    backdrop.style.cursor = overAnchor ? 'pointer' : 'default';
  }

  private onDocumentPointer(e: Event): void {
    const target = e.target as Node;
    const panel = this.panelRef && this.panelRef.nativeElement;
    // Clique dentro do painel ou no próprio gatilho → não fecha.
    if (panel && panel.contains(target)) { return; }
    if (this.anchor && this.anchor.contains(target)) { return; }
    this.zone.run(() => this.hide());
  }

  private onKeydown(e: KeyboardEvent): void {
    if (e.key === 'Escape' || e.key === 'Esc') {
      e.stopPropagation();
      this.zone.run(() => this.hide());
    }
  }

  /** Posicionamento próprio: abaixo por padrão; flip vertical se faltar espaço; shift horizontal pra caber. */
  private position(): void {
    const panel = this.panelRef && this.panelRef.nativeElement;
    if (!panel || !this.anchor) { return; }
    const a = this.anchor.getBoundingClientRect();
    const pw = panel.offsetWidth;
    const ph = panel.offsetHeight;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const pad = this.viewportPadding;
    const wantsBottom = this.placement.startsWith('bottom');

    const spaceBelow = vh - a.bottom - this.offset;
    const spaceAbove = a.top - this.offset;
    // Decide vertical: respeita o placement, mas flipa se não couber e couber do outro lado.
    let placeBelow = wantsBottom;
    if (wantsBottom && ph > spaceBelow && ph <= spaceAbove) { placeBelow = false; }
    else if (!wantsBottom && ph > spaceAbove && ph <= spaceBelow) { placeBelow = true; }
    let top = placeBelow ? a.bottom + this.offset : a.top - this.offset - ph;
    top = Math.max(pad, Math.min(top, vh - ph - pad));

    // Horizontal: '-start' alinha a borda esquerda do gatilho; '-end' a direita.
    const alignEnd = this.placement.endsWith('end');
    let left = alignEnd ? a.right - pw : a.left;
    left = Math.max(pad, Math.min(left, vw - pw - pad));

    const wantLeft = Math.round(left);
    const wantTop = Math.round(top);
    panel.style.position = 'fixed';

    // `position: fixed` é relativo à VIEWPORT — EXCETO quando um ancestral do
    // painel tem transform/filter/will-change/contain: esse ancestral vira o
    // containing block (e o offsetParent) do fixed, e left/top passam a ser
    // relativos a ELE. O painel é portado pro <body>, mas se <body>/<html> (ou,
    // num cenário onde o painel não chegou ao body, um ancestral na página)
    // criar esse containing block, precisamos descontar a origem dele.
    //
    // offsetParent null/body/html ⇒ a origem É a viewport (offset 0). Para um
    // elemento real, subtraímos o seu padding-box (getBoundingClientRect +
    // clientLeft/Top do offsetParent — estável, não é afetado pela animação de
    // scale do PRÓPRIO painel). A tentativa anterior usava `panel.offsetTop`,
    // que vale ~`style.top` → delta ≈ 0 nesse caso, deixando o painel deslocado
    // pela posição do containing block (menu "abrindo longe" em página rolada).
    const containingBlock = panel.offsetParent as HTMLElement | null;
    let originX = 0;
    let originY = 0;
    if (containingBlock && containingBlock !== document.body && containingBlock !== document.documentElement) {
      const cbRect = containingBlock.getBoundingClientRect();
      originX = cbRect.left + containingBlock.clientLeft;
      originY = cbRect.top + containingBlock.clientTop;
    }
    panel.style.left = `${wantLeft - originX}px`;
    panel.style.top = `${wantTop - originY}px`;
    // Origem da animação (scale) coerente com a direção que o menu abriu:
    // pra baixo → origin top center; pra cima → origin bottom center.
    panel.classList.toggle('opens-up', !placeBelow);
  }

  private focusPanel(): void {
    const panel = this.panelRef && this.panelRef.nativeElement;
    if (!panel) { return; }
    // Foca SÓ se houver `[autofocus]` explícito no conteúdo. Auto-focar
    // o 1º item dispara :focus que Material pinta com bg-cinza, fazendo
    // o item parecer "selecionado" sem o usuário ter interagido. Foco
    // por teclado (Tab/Arrow) continua funcionando normalmente.
    const explicit = panel.querySelector<HTMLElement>('[autofocus]');
    (explicit || panel).focus({ preventScroll: true });
  }

  private returnFocus(): void {
    if (this.anchor && typeof this.anchor.focus === 'function') {
      this.anchor.focus({ preventScroll: true });
    }
  }

  private setAnchorExpanded(v: boolean): void {
    if (this.anchor) { this.anchor.setAttribute('aria-expanded', String(v)); }
  }
}
